import { Button, Flex, Space, Text } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useOpenConnectBankModal } from '@features/bankAccounts/hooks/useOpenConnectBankModal'
import { requestConnectBankAutoOpen } from '@features/bankAccounts/utils/connectBankModalUiSourceStore'
import { useBankTransactionsAccess } from '@features/bankTransactions/hooks/useBankTransactionsAccess'

import { BankReconciliationConnectBank } from '../../../../../../assets/images'
import { useSegment } from '../../../../../../hooks'
import { useOrganizationViewUrl } from '../../../../../../hooks/routing/useOrganizationViewUrl'
import { useSplitTest } from '../../../../../../hooks/useSplitTest'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { widgetId } from '../../../../constants/widgetId'
import { useWidgetsActions } from '../../../../hooks/useWidgetsActions'
import { BankIntegrationActionPoint } from '../../enums/bankIntegrationActionPoint'
import { getBankDisconnectionWidgetContent } from '../../utils/getBankDisconnectionWidgetContent'
import * as Styled from './styles'

// Variant 1 has buckets 0-5
const variant2Buckets = [6, 7, 8, 9, 10, 11]

interface BankDisconnectionProps {
  isCpa?: boolean
}

export const BankDisconnection = ({ isCpa = false }: BankDisconnectionProps) => {
  const { t } = useTranslation()
  const { isEnabled: isVariant2, isLoading } = useSplitTest({ buckets: variant2Buckets })
  const { trackDashboardActionPerformed } = useWidgetsActions()
  const { isTransactionsOnboardingEnabled } = useBankTransactionsAccess()
  const { url: bankAccountsUrl } = useOrganizationViewUrl(reactRoute.organization.bank.getBankAccountsRoute())
  const { track } = useSegment()

  const { title, infoText, trackingEvent, uiSource } = getBankDisconnectionWidgetContent({ isCpa, isVariant2 })
  const openConnectToBankModal = useOpenConnectBankModal()

  const handleClick = useCallback(() => {
    trackDashboardActionPerformed({
      action: BankIntegrationActionPoint.ConnectBank,
      widgetId: widgetId.BankIntegration,
    })
    if (trackingEvent) {
      track(trackingEvent)
    }

    if (isTransactionsOnboardingEnabled) {
      requestConnectBankAutoOpen(uiSource)
    } else {
      openConnectToBankModal(uiSource)
    }
  }, [
    trackDashboardActionPerformed,
    track,
    trackingEvent,
    isTransactionsOnboardingEnabled,
    openConnectToBankModal,
    uiSource,
  ])

  if (isLoading && !isCpa) {
    return null
  }

  return (
    <Styled.ContentWrapper>
      <Flex flexDirection="column" justifyContent="space-between">
        <Flex flexDirection="column">
          <Text variant="h1">{t(title)}</Text>
          <Space size="s8" />
          <Text variant="bodyMD">{t(infoText)}</Text>
        </Flex>
        <div>
          <Button onClick={handleClick} icon="bank" to={isTransactionsOnboardingEnabled ? bankAccountsUrl : undefined}>
            {t('dashboard.widget.bank-integration.connect_bank')}
          </Button>
        </div>
      </Flex>
      <Styled.IllustrationContainer>
        <BankReconciliationConnectBank aria-hidden />
      </Styled.IllustrationContainer>
    </Styled.ContentWrapper>
  )
}
